import type { ApiClient } from "../api/client";
import { showToast } from "../ui/toast";
import { start, getConnection, test as testConn, disconnect, type GwConnectionStatus } from "./connect-api";

export interface GoogleConnectDeps {
  readonly apiClient?: ApiClient;
  readonly navigate?: (url: string) => void; // 既定は window.location.assign（テストで差替）
}

export function renderGoogleConnect(root: HTMLElement, deps: GoogleConnectDeps = {}): void {
  root.replaceChildren();
  const card = document.createElement("div");
  card.className = "max-w-xl rounded-lg border border-slate-200 bg-white p-6";
  const h2 = document.createElement("h2");
  h2.className = "text-base font-semibold text-slate-900";
  h2.textContent = "Google Workspace と接続";
  const desc = document.createElement("p");
  desc.className = "mt-1 text-sm text-slate-600";
  desc.textContent = "OAuth（管理者同意）で接続します。Workspace の管理者でサインインしてください。読み取り専用のスコープのみを要求します。";
  const body = document.createElement("div");
  body.className = "mt-4";
  card.append(h2, desc, body);
  root.append(card);

  const navigate = deps.navigate ?? ((url: string) => window.location.assign(url));
  const apiClient = deps.apiClient;
  if (apiClient === undefined) {
    body.textContent = "接続機能を利用するにはサインインが必要です。";
    return;
  }

  function connectButton(): HTMLButtonElement {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "rounded bg-shinonome-700 px-4 py-2 font-medium text-white hover:bg-shinonome-800 disabled:opacity-60";
    btn.textContent = "Google Workspace と接続";
    btn.addEventListener("click", () => {
      btn.disabled = true;
      start({ apiClient: apiClient! })
        .then((r) => navigate(r.authorizeUrl))
        .catch(() => { showToast({ tone: "warning", title: "接続を開始できませんでした", message: "時間をおいて再度お試しください。" }); btn.disabled = false; });
    });
    return btn;
  }

  function renderConnected(conn: GwConnectionStatus): void {
    body.replaceChildren();
    const info = document.createElement("p");
    info.className = "text-sm text-slate-700";
    info.textContent = `接続済み: ${conn.domain ?? ""}（${conn.connectedAt ? new Date(conn.connectedAt).toLocaleString("ja-JP") : "-"}）`;
    const testBtn = document.createElement("button");
    testBtn.type = "button"; testBtn.className = "mr-2 rounded border border-slate-300 px-3 py-2 text-sm";
    testBtn.textContent = "接続テスト";
    testBtn.addEventListener("click", () => {
      testBtn.disabled = true;
      testConn({ apiClient: apiClient! }).then((r) => showToast({ tone: r.status === "connected" ? "success" : "warning", title: "接続テスト", message: `状態: ${r.status}` }))
        .catch(() => showToast({ tone: "warning", title: "接続テスト失敗", message: "時間をおいて再度お試しください。" }))
        .finally(() => { testBtn.disabled = false; load(); });
    });
    const disBtn = document.createElement("button");
    disBtn.type = "button"; disBtn.className = "rounded border border-red-300 px-3 py-2 text-sm text-red-700";
    disBtn.textContent = "切断";
    disBtn.addEventListener("click", () => {
      disBtn.disabled = true;
      disconnect({ apiClient: apiClient! }).then(() => { showToast({ tone: "info", title: "切断しました", message: "Google Workspace 接続を解除しました。" }); load(); })
        .catch(() => { showToast({ tone: "warning", title: "切断に失敗しました", message: "時間をおいて再度お試しください。" }); disBtn.disabled = false; });
    });
    const row = document.createElement("div"); row.className = "mt-4"; row.append(testBtn, disBtn);
    body.append(info, row);
  }

  function load(): void {
    getConnection({ apiClient: apiClient! })
      .then((conn) => { if (conn.status === "connected") renderConnected(conn); else { body.replaceChildren(connectButton()); } })
      .catch(() => { body.replaceChildren(connectButton()); });
  }
  load();
}
